/**
 * @fileoverview `WorksModule` (WEB-712) — the art category's works-on-view
 * checklist. Registered against `EventModuleKind = 'works'` in
 * `EventModules/registry.tsx`.
 *
 * Design: `docs/design-system/event-v2-m2-module-specs.md` §3.1. Shared row
 * list (WEB-707, `ModuleRowList`) with `visual: { kind: 'thumb' }` rows —
 * title is the work, `meta` is `MAKER · YEAR · MEDIUM`, `trail` is the room.
 * No lead column; a footer "more" link is supplied by the lexicon entry, not
 * this file (`EventModuleSection` renders it from `module.moreHref`).
 *
 * ## Degrade case — the whole module, always, today
 *
 * `EventDetailV2` (`Event` intersected with `editorial` / `meta` / `atGlance`
 * / `decisionPanel?` / `qualityStatus?` / `artists?`) carries no
 * works-checklist field anywhere on the wire — verified against the
 * generated OpenAPI schema. `lib/eventDetail/modules.ts`'s
 * `hasModuleContent` resolves `works` to `hasContent: false`
 * unconditionally for exactly this reason ("Awaiting supply … until a
 * future contract change adds the field"). This module therefore renders
 * nothing in production today — that IS the design's own degrade case
 * (spec §3.1: "LOCI has no works checklist. Whole module gates off").
 *
 * `resolveWorksRows` below reads a `works` field through the same
 * narrow-structural-read pattern `EventSpineTemplate`'s `SpineVenueLocation`
 * uses for a field the generated type doesn't (yet) capture. It documents
 * the shape a future contract change would add and keeps the row-mapping
 * itself genuinely testable ahead of that change
 * (`__tests__/WorksModule.test.tsx` stubs `hasContent: true` plus an event
 * carrying the field), rather than leaving the mapping unwritten and
 * unverified until a real backend column exists. `module.hasContent` stays
 * the only gate this component ever checks — it never re-derives gating
 * from `event`, per the registry contract (`EventModuleProps` doc comment).
 */

import type { ReactElement } from 'react';

import {
  EventModuleSection,
  ModuleRowList,
  type ModuleRow,
} from '@/components/organisms/EventModuleSection';
import type { EventDetailV2 } from '@/types/eventDetail';

import type { EventModuleProps } from './registry';

/**
 * The shape a future works-checklist contract change would add to
 * `EventDetailV2`. Not present on the generated schema today — see the file
 * header.
 */
interface WorksChecklistItem {
  readonly id: string;
  readonly title: string;
  readonly maker: string;
  readonly year: string;
  readonly medium: string;
  readonly room: string;
}

/** The one extra field {@link resolveWorksRows} reads structurally. */
interface EventWithWorksChecklist {
  works?: readonly WorksChecklistItem[];
}

/**
 * Maps a works checklist into `ModuleRowList` rows. `event` carries this
 * field on no production payload today (see the file header) — the read is
 * defensive and always resolves to an empty array outside a stubbed test.
 */
function resolveWorksRows(event: EventDetailV2): ModuleRow[] {
  const items = (event as unknown as EventWithWorksChecklist).works ?? [];
  return items.map((item) => ({
    id: item.id,
    visual: { kind: 'thumb' },
    title: item.title,
    meta: `${item.maker} · ${item.year} · ${item.medium}`,
    trail: item.room,
  }));
}

/**
 * `WorksModule` — registered against `works`. Trusts `module.hasContent`
 * (authoritative — see the registry contract) and renders nothing when it
 * is `false`, which is every production render today.
 */
export function WorksModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const rows = resolveWorksRows(event);

  return (
    <EventModuleSection module={module} chroma={chroma}>
      <ModuleRowList rows={rows} chroma={chroma} />
    </EventModuleSection>
  );
}
